/* Copyright (c) 2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { NgZone } from '@angular/core';
import { merge, Observable, EMPTY, combineLatest, Subject, BehaviorSubject, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, mapTo, filter, mergeMap } from 'rxjs/operators';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { repeatedPress } from './gamepad-common-utils';
import { NvGamepadConfig } from './gamepad.config';
import { GamepadService } from './gamepad.service';
import { GamepadButtonName, MonitorOptions } from './gamepad.types';

type GetConnectedGamepadsReturn = Types.Gfn.Streaming.GetConnectedGamepadsReturn;
type ConnectedGamepadInfo = Types.Gfn.Streaming.ConnectedGamepadInfo;
type GamepadEventInfo = Types.Gfn.Streaming.RegisterGamepadEventCallbackReturn;

enum NavigationDirection {
    Left = 'left',
    Right = 'right',
    Up = 'up',
    Down = 'down'
}

enum NavigationAxis {
    Vertical = 'vertical',
    Horizontal = 'horizontal'
}

interface ButtonPressedInfo {
    name: GamepadButtonName;
    isPressed: boolean;
}

interface JoystickMovement {
    axis: NavigationAxis;
    value: number;
    position: JoystickPosition;
}

enum JoystickPosition {
    Left,
    Right
}

const GamepadButtonReleasedValue = 0;
const MovementMaxValue = 32767;
const MovementMinValue = -32768;

const DirectionToAxisMap = {
    [NavigationDirection.Down]: NavigationAxis.Vertical,
    [NavigationDirection.Up]: NavigationAxis.Vertical,
    [NavigationDirection.Left]: NavigationAxis.Horizontal,
    [NavigationDirection.Right]: NavigationAxis.Horizontal
};

// This is picked from Geronimo InputEvent.h
enum NativeGamepadEvent {
    /// Reserved value for invalid gamepad control.
    NVST_GC_NONE = 0,
    NVST_GC_BTN_START = 1,
    NVST_GC_BTN_SELECT = 2,
    NVST_GC_BTN_MODE = 3,
    NVST_GC_BTN_X = 4,
    NVST_GC_BTN_Y = 5,
    NVST_GC_BTN_Z = 6,
    NVST_GC_BTN_A = 7,
    NVST_GC_BTN_B = 8,
    NVST_GC_BTN_C = 9,
    NVST_GC_BTN_THUMBL = 10,
    NVST_GC_BTN_THUMBR = 11,
    NVST_GC_BTN_L1 = 12,
    NVST_GC_BTN_R1 = 13,
    NVST_GC_AXIS_DPAD_X = 14,
    NVST_GC_AXIS_DPAD_Y = 15,
    NVST_GC_AXIS_LSTICK_X = 16,
    NVST_GC_AXIS_LSTICK_Y = 17,
    NVST_GC_AXIS_RSTICK_X = 18,
    NVST_GC_AXIS_RSTICK_Y = 19,
    NVST_GC_TRIGGER_LEFT = 20,
    NVST_GC_TRIGGER_RIGHT = 21,
    NVST_GC_BTN_TOUCHPAD = 22,
    NVST_GC_AXIS_TOUCHPAD1_X = 23,
    NVST_GC_AXIS_TOUCHPAD1_Y = 24,
    NVST_GC_AXIS_TOUCHPAD2_X = 25,
    NVST_GC_AXIS_TOUCHPAD2_Y = 26,
    /// Reserved value for count of supported gamepad controls.
    NVST_GC_NUM_CONTROLS
}

const NativeGamepadToWebGamepadMap = {
    [NativeGamepadEvent.NVST_GC_BTN_START]: GamepadButtonName.Start,
    [NativeGamepadEvent.NVST_GC_BTN_SELECT]: GamepadButtonName.Back,
    [NativeGamepadEvent.NVST_GC_BTN_MODE]: GamepadButtonName.Center,
    [NativeGamepadEvent.NVST_GC_BTN_X]: GamepadButtonName.X,
    [NativeGamepadEvent.NVST_GC_BTN_Y]: GamepadButtonName.Y,
    [NativeGamepadEvent.NVST_GC_BTN_A]: GamepadButtonName.A,
    [NativeGamepadEvent.NVST_GC_BTN_B]: GamepadButtonName.B,
    [NativeGamepadEvent.NVST_GC_BTN_THUMBL]: GamepadButtonName.LeftStick,
    [NativeGamepadEvent.NVST_GC_BTN_THUMBR]: GamepadButtonName.RightStick,
    [NativeGamepadEvent.NVST_GC_BTN_L1]: GamepadButtonName.LeftBumper,
    [NativeGamepadEvent.NVST_GC_BTN_R1]: GamepadButtonName.RightBumper,
    [NativeGamepadEvent.NVST_GC_TRIGGER_LEFT]: GamepadButtonName.LeftTrigger,
    [NativeGamepadEvent.NVST_GC_TRIGGER_RIGHT]: GamepadButtonName.RightTrigger
};

export class GeronimoEventGamepadService extends GamepadService {
    private logger: Logger;
    private buttonPressedSubject = new Subject<ButtonPressedInfo>();
    private joystickMovementSubject = new Subject<JoystickMovement>();
    private connectedGamepadsInfoSubject = new BehaviorSubject<ConnectedGamepadInfo[]>([]);

    get isGamepadConnected$(): Observable<boolean> {
        return this.connectedGamepadsInfoSubject.asObservable().pipe(
            map(info => {
                return info.length > 0;
            })
        );
    }

    get isAnyGamepadInUse$(): Observable<boolean> {
        const isAnyButtonPressed$ = this.buttonPressedSubject.asObservable().pipe(
            map(value => value.isPressed),
            startWith(false)
        );

        const isRightJoystickInUse$ = this.joystickMovementSubject.asObservable().pipe(
            filter(state => state.position === JoystickPosition.Right),
            map(state => {
                return (
                    state.value > this.gamepadConfig.joystickMovementThreshold * MovementMaxValue ||
                    state.value < this.gamepadConfig.joystickMovementThreshold * MovementMinValue
                );
            }),
            startWith(false)
        );

        const isLeftJoystickInUse$ = this.joystickMovementSubject.asObservable().pipe(
            filter(state => state.position === JoystickPosition.Left),
            map(state => {
                return (
                    state.value > this.gamepadConfig.joystickMovementThreshold * MovementMaxValue ||
                    state.value < this.gamepadConfig.joystickMovementThreshold * MovementMinValue
                );
            }),
            startWith(false)
        );

        const gamepadInUse$ = combineLatest([isRightJoystickInUse$, isLeftJoystickInUse$, isAnyButtonPressed$]).pipe(
            map(([isRightJoystickInUse, isLeftJoystickInUse, buttonPressed]: [boolean, boolean, boolean]) => {
                return isRightJoystickInUse || isLeftJoystickInUse || buttonPressed;
            }),
            distinctUntilChanged()
        );

        return gamepadInUse$;
    }

    private getObservableForDirection(direction: NavigationDirection): Observable<boolean> {
        let dpadButtonName: GamepadButtonName;
        switch (direction) {
            case NavigationDirection.Down:
                dpadButtonName = GamepadButtonName.DpadDown;
                break;
            case NavigationDirection.Left:
                dpadButtonName = GamepadButtonName.DpadLeft;
                break;
            case NavigationDirection.Right:
                dpadButtonName = GamepadButtonName.DpadRight;
                break;
            case NavigationDirection.Up:
                dpadButtonName = GamepadButtonName.DpadUp;
                break;
        }
        return this.isGamepadConnected$.pipe(
            switchMap(isConnected => {
                if (!isConnected) {
                    return EMPTY;
                }
                return merge(
                    this.buttonPressedSubject.asObservable().pipe(
                        filter(state => state.name === dpadButtonName),
                        map(state => state.isPressed)
                    ),
                    this.getLeftJoystickMovementObservable(direction)
                ).pipe(distinctUntilChanged(), repeatedPress(this.gamepadConfig.continuousNavigationInterval));
            })
        );
    }

    get leftPressed$(): Observable<boolean> {
        return this.getObservableForDirection(NavigationDirection.Left);
    }

    get rightPressed$(): Observable<boolean> {
        return this.getObservableForDirection(NavigationDirection.Right);
    }

    get upPressed$(): Observable<boolean> {
        return this.getObservableForDirection(NavigationDirection.Up);
    }

    get downPressed$(): Observable<boolean> {
        return this.getObservableForDirection(NavigationDirection.Down);
    }

    get rightStickMoved$(): Observable<boolean> {
        return this.joystickMovementSubject.asObservable().pipe(
            filter(state => state.position === JoystickPosition.Right),
            map(state => {
                return (
                    state.value > this.gamepadConfig.joystickMovementThreshold * MovementMaxValue ||
                    state.value < this.gamepadConfig.joystickMovementThreshold * MovementMinValue
                );
            }),
            distinctUntilChanged()
        );
    }

    onButtonPressed$(button: GamepadButtonName, options?: MonitorOptions): Observable<boolean> {
        return this.buttonPressedSubject.asObservable().pipe(
            filter(state => state.name === button),
            map(state => state.isPressed)
        );
    }

    onButtonLongPressed$(button: GamepadButtonName, longPressStartDuration?: number): Observable<boolean> {
        return this.buttonPressedSubject.asObservable().pipe(
            filter(value => value.name === button),
            switchMap(value => {
                return value.isPressed
                    ? timer(longPressStartDuration ?? this.gamepadConfig.longPressDuration).pipe(mapTo(true))
                    : EMPTY;
            })
        );
    }

    onButtonComboPressed$(buttons: GamepadButtonName[]): Observable<boolean> {
        const buttonPressedObservables: Observable<boolean>[] = buttons.map(button => {
            return this.buttonPressedSubject.asObservable().pipe(
                filter(value => value.name === button),
                map(value => value.isPressed),
                startWith(false)
            );
        });
        return combineLatest(buttonPressedObservables).pipe(
            map(values => {
                const isSomeButtonNotPressed = values.some(value => value === false);
                return isSomeButtonNotPressed ? false : true;
            })
        );
    }

    private hasMovedMoreThanThreshold(value: number, direction: NavigationDirection): boolean {
        switch (direction) {
            case NavigationDirection.Down:
            case NavigationDirection.Right:
                return value > this.gamepadConfig.joystickMovementThreshold * MovementMaxValue;
            case NavigationDirection.Up:
            case NavigationDirection.Left:
                return value < this.gamepadConfig.joystickMovementThreshold * MovementMinValue;
        }
    }

    private hasMovedMoreThanPreviousValue(
        direction: NavigationDirection,
        currentValue: number,
        previousValue: number
    ): boolean {
        switch (direction) {
            case NavigationDirection.Down:
            case NavigationDirection.Right:
                return currentValue > previousValue;
            case NavigationDirection.Left:
            case NavigationDirection.Up:
                return currentValue < previousValue;
        }
    }

    private getLeftJoystickMovementObservable(direction: NavigationDirection): Observable<boolean> {
        const axis = DirectionToAxisMap[direction];
        let previousMovementValue;
        let cooloffDone = true;
        return this.joystickMovementSubject.asObservable().pipe(
            filter(state => {
                return state.position === JoystickPosition.Left && state.axis === axis;
            }),
            map(state => {
                let navigate = false;
                if (previousMovementValue === undefined) {
                    navigate = this.hasMovedMoreThanThreshold(state.value, direction);
                    if (navigate) {
                        if (!cooloffDone) {
                            return false;
                        }
                        previousMovementValue = state.value;
                    }
                    return navigate;
                }
                if (this.hasMovedMoreThanPreviousValue(direction, state.value, previousMovementValue)) {
                    previousMovementValue = state.value;
                    return true;
                }
                previousMovementValue = undefined;
                cooloffDone = false;
                setTimeout(() => {
                    cooloffDone = true;
                }, this.gamepadConfig.cooloffDuration);
                return false;
            })
        );
    }

    private handleGamepadEvent(event: GamepadEventInfo): void {
        const mappedButtonName = NativeGamepadToWebGamepadMap[event.control];
        if (mappedButtonName !== undefined) {
            this.buttonPressedSubject.next({
                name: mappedButtonName,
                isPressed: event.value === 1 || event.value === -1
            });
            return;
        }
        switch (event.control) {
            case NativeGamepadEvent.NVST_GC_AXIS_DPAD_X:
                if (event.value === MovementMinValue) {
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadLeft,
                        isPressed: true
                    });
                } else if (event.value === MovementMaxValue) {
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadRight,
                        isPressed: true
                    });
                } else if (event.value === GamepadButtonReleasedValue) {
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadLeft,
                        isPressed: false
                    });
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadRight,
                        isPressed: false
                    });
                }
                break;
            case NativeGamepadEvent.NVST_GC_AXIS_DPAD_Y:
                if (event.value === MovementMinValue) {
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadUp,
                        isPressed: true
                    });
                } else if (event.value === MovementMaxValue) {
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadDown,
                        isPressed: true
                    });
                } else if (event.value === GamepadButtonReleasedValue) {
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadDown,
                        isPressed: false
                    });
                    this.buttonPressedSubject.next({
                        name: GamepadButtonName.DpadUp,
                        isPressed: false
                    });
                }
                break;
            case NativeGamepadEvent.NVST_GC_AXIS_LSTICK_X:
                this.joystickMovementSubject.next({
                    value: event.value,
                    axis: NavigationAxis.Horizontal,
                    position: JoystickPosition.Left
                });
                break;
            case NativeGamepadEvent.NVST_GC_AXIS_LSTICK_Y:
                this.joystickMovementSubject.next({
                    value: event.value,
                    axis: NavigationAxis.Vertical,
                    position: JoystickPosition.Left
                });
                break;
            case NativeGamepadEvent.NVST_GC_AXIS_RSTICK_X:
                this.joystickMovementSubject.next({
                    value: event.value,
                    axis: NavigationAxis.Horizontal,
                    position: JoystickPosition.Right
                });
                break;
            case NativeGamepadEvent.NVST_GC_AXIS_RSTICK_Y:
                this.joystickMovementSubject.next({
                    value: event.value,
                    axis: NavigationAxis.Vertical,
                    position: JoystickPosition.Right
                });
                break;
            default:
                this.logger.warn(`No handling for gamepad event ${event.control}, ignoring`);
                break;
        }
    }

    constructor(
        loggingService: LoggingService,
        private bridgeService: BridgeService,
        private gamepadConfig: NvGamepadConfig,
        private ngZone: NgZone
    ) {
        super();
        this.logger = loggingService.getLogger('GeronimoEventGamepadService');

        this.ngZone.runOutsideAngular(() => {
            this.bridgeService
                .executeCommand(Commands.Gfn.Streaming.RegisterGamepadChangedCallback)
                .pipe(
                    startWith(0),
                    mergeMap(_ => this.bridgeService.executeCommand(Commands.Gfn.Streaming.GetConnectedGamepads))
                )
                .subscribe((info: GetConnectedGamepadsReturn) => {
                    this.logger.info(`Connected gamepad info post gamepadChangeEvent: ${JSON.stringify(info)}`);
                    this.connectedGamepadsInfoSubject.next(info.connectedGamepadInfoList);
                });

            this.bridgeService
                .executeCommand(Commands.Gfn.Streaming.RegisterGamepadEventCallback, {
                    minPositiveMovement: Math.floor(this.gamepadConfig.joystickTriggeredTolerance * MovementMaxValue),
                    maxNegativeMovement: Math.floor(this.gamepadConfig.joystickTriggeredTolerance * MovementMinValue)
                })
                .subscribe(event => {
                    this.handleGamepadEvent(event);
                });
        });
    }
}
